import { useRef, useLayoutEffect, useState, useMemo } from "react";

// Based on https://github.com/gyto/react-use-tag-truncator/

/**
 * Truncates a list of items when a certain height is reached. The container
 * should be a ul tag and the items should be contained in li tags
 *
 * Limitations: will not work if the container resizes independently from the
 * window
 */
export const useTagTruncator = ({
    maxHeight,
    deps,
}: {
    maxHeight: number | string;
    /** Dependency that will re-trigger the truncation */
    deps?: any[];
}) => {
    const _deps = useMemo(() => (deps ? deps : []), [deps]);
    const ref = useRef<HTMLUListElement>(null);
    const [isExtended, setExtend] = useState<boolean>(false);
    const [hiddenCount, setHiddenCount] = useState<number>();

    useLayoutEffect(() => {
        if (isExtended) {
            reveal(ref.current);
            setHiddenCount(undefined);
        } else {
            const resizeCb = () => {
                const hiddenCount = truncate(ref.current);
                setHiddenCount(hiddenCount);
            };
            resizeCb();
            window.addEventListener("resize", resizeCb);
            return () => {
                window.addEventListener("resize", resizeCb);
            };
        }
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ..._deps,
        isExtended,
        maxHeight,
    ]);

    const returnValue = useMemo(
        () => ({
            containerProps: {
                ref,
                style: {
                    overflow: "hidden",
                    maxHeight: isExtended ? undefined : maxHeight,
                },
            },
            isExtended,
            toggleExtend: () => setExtend(!isExtended),
            hiddenCount,
        }),
        [hiddenCount, isExtended, maxHeight]
    );

    return returnValue;
};
export default useTagTruncator;

const truncate = (container: HTMLElement | null): number | undefined => {
    // Strategy: Hide all tags. Then unhide them one by one. If unhiding one
    // causes an overflow out of the container, rehide it and leave all
    // following tags hidden. Keeping in mind some children are not tags (the
    // hidden count, for example), checking for overflow should be done on all
    // elements in the child list following the unhidden tag.

    if (!container) return undefined;

    const children = Array.from(container.children) as HTMLElement[];

    children.forEach((child) => {
        if (isTag(child)) child.hidden = true;
    });

    for (let index = 0; index < children.length; index++) {
        const element = children[index];
        if (!isTag(element)) continue;

        element.hidden = false;
        if (hasOverflow(container, children.slice(index))) {
            element.hidden = true;
            break;
        }
    }

    const hiddenItems = children.filter((h) => h.hidden).length;

    return hiddenItems;
};

const isTag = (element: HTMLElement): boolean => element.tagName === "LI";

const hasOverflow = (
    container: HTMLElement,
    children: HTMLElement[]
): boolean => {
    const containerRect = container.getBoundingClientRect();

    return children.some((child) => {
        if (child.hidden) return false;
        const childRect = child.getBoundingClientRect();
        if (
            childRect.bottom > containerRect.bottom ||
            childRect.right > containerRect.right
        )
            return true;
        return false;
    });
};

const reveal = (container: HTMLElement | null) => {
    if (!container) return undefined;
    (Array.from(container.children) as HTMLElement[]).forEach((element) => {
        if (isTag(element)) element.hidden = false;
    });
};
